import React, { useEffect, useState } from 'react';
import { API_URL } from '../services/api.js'; 


const PaymentStep = ({ camp, participantName, onPaymentComplete }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [qrImage, setQrImage] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [qrError, setQrError] = useState(false);

  const displayName = camp.displayName || camp.name || 'Läger';
  const message = `${displayName}, ${participantName}`.substring(0, 50);
  const amount = camp.price || 0;
  const swishNumber = camp.swishNumber || '';

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      setIsMobile(/android|iPad|iPhone|iPod/i.test(userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch QR code
  useEffect(() => {
    if (isMobile || !swishNumber || !amount) return;

    const fetchQR = async () => {
      setQrLoading(true);
      setQrError(false);
      try {
        const url = `${API_URL}?` + new URLSearchParams({
          action: 'qr',
          payee: swishNumber,
          amount: String(amount),
          message: message,
          size: '400' // Bigger QR
        });

        const res = await fetch(url);
        const data = await res.json();

        if (data.qr) {
          setQrImage(data.qr);
        } else {
          setQrError(true);
        }
      } catch (e) {
        setQrError(true);
      } finally {
        setQrLoading(false);
      }
    };
    fetchQR();
  }, [isMobile, swishNumber, amount, message]);

  const swishLink = `swish://payment?data=${encodeURIComponent(JSON.stringify({
    version: 1,
    payee: { value: swishNumber, editable: false },
    amount: { value: amount, editable: false },
    message: { value: message, editable: false }
  }))}`;

  return (
    <div className="max-w-md mx-auto animate-in fade-in duration-500">
      <style>{`
        .swish-card { 
          background: #fff; 
          border: 1px solid #e2e8f0; 
          border-radius: 16px; 
          padding: 20px; 
          box-shadow: 0 1px 3px rgba(0,0,0,0.05); 
          margin-bottom: 24px;
           }
        @media (min-width: 640px) {
          .swish-card { padding: 32px; margin-bottom: 32px; }
        }
        .swish-card h4 { 
          font-size: 0.875rem; 
          font-weight: 800; 
          letter-spacing: 1.5px; 
          text-transform: uppercase; 
          color: #003360; 
          margin-bottom: 24px; 
          text-align: center; 
        }
        .qr-wrap { display: flex; justify-content: center; margin-bottom: 24px; }
        .qr-wrap img { width: 180px; height: 180px; border-radius: 12px; border: 1px solid #f1f5f9; display: block; }
        .qr-loading { width: 180px; height: 180px; border-radius: 12px; border: 1px dashed #e2e8f0; display: flex; align-items: center; justify-content: center; background: #f8fafc; color: #94a3b8; font-size: 0.75rem; }
        .swish-or { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; font-size: 0.7rem; color: #94abcb; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px; }
        .swish-or::before, .swish-or::after { content: ""; flex: 1; height: 1px; background: #f1f5f9; }
        .swish-app-btn { display: inline-flex; align-items: center; justify-content: center; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; height: 56px; width: 100%; text-decoration: none; cursor: pointer; transition: all 0.2s; margin-bottom: 24px; }
        .swish-app-btn:hover { border-color: #ee6f9c; background-color: #fff5f8; }
        .swish-app-btn img { height: 28px; width: auto; display: block; }
        .swish-details { border-top: 1px solid #f8fafc; padding-top: 20px; }
         .swish-detail-row { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; font-size: 0.9rem; padding: 8px 0; }
        .lbl { flex: 0 0 auto; color: #94a3b8; font-weight: 600; }
        .val { flex: 1 1 auto; min-width: 0; text-align: right; font-weight: 800; color: #003360; overflow-wrap: anywhere; word-break: break-word; }
      `}</style>

      <div className="swish-card">
        <h4>Betala med Swish</h4>

        {!isMobile && (
          <>
            <div className="qr-wrap">
              {qrLoading && <div className="qr-loading text-center px-4">Laddar QR-kod...</div>}
              {!qrLoading && qrImage && <img src={qrImage} alt="Swish QR" />}
              {!qrLoading && qrError && <div className="qr-loading text-red-500 p-4 text-center">QR-fel</div>}
            </div>
            <div className="swish-or">Eller betala manuellt</div>
          </>
        )}

        {isMobile && (
          <a href={swishLink} className="swish-app-btn">
            <img src="/swish-logo.svg" alt="Öppna Swish" />
          </a>
        )}

        <div className="swish-details">
          <div className="swish-detail-row">
            <span className="lbl">Nummer</span>
            <span className="val">{swishNumber}</span>
          </div>
          <div className="swish-detail-row">
            <span className="lbl">Belopp</span>
            <span className="val">{amount} kr</span>
          </div>
          <div className="swish-detail-row">
            <span className="lbl">Meddelande</span>
            <span className="val">{message}</span>
          </div>
        </div>
      </div>

      <button
        onClick={onPaymentComplete}
        className="w-full bg-[#003360] text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-all shadow-sm active:scale-95"
      >
        Jag har swishat
      </button>
    </div>
  );
};

export default PaymentStep;
